import { useCallback, useState } from "react";
import styled, { createGlobalStyle } from "styled-components/macro";
import { ProcessTilesConfigMenu } from "./ProcessTilesExpandedTilesConfig";
import { IconButton, Tooltip } from "@mui/material";
import {
  BREAKPOINT_TABLET,
  Z_INDEX,
  useBreakpoints,
} from "constants/constants";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import {
  ProcessTilesFilterControls,
  ProcessTilesSortFilterGroupByDropdowns,
} from "./ProcessTilesFilterControls";
import { MoreVert } from "@mui/icons-material";
import { useExpandedTileFlags } from "components/Dashboard/Tiles/ProcessTile/processTileUtils";
import { ProcessTilesFilterIndicator } from "./ProcessTilesFilterIndicator";
import { ProcessTilesFilterControlsStyles } from "./ProcessTilesFilterControlsStyles";
import { useIsDarkMode } from "store/atoms";

const ProcessTilesControls = () => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const { isCustomerWithExpandableTile } = useExpandedTileFlags(null);
  const { isTabletOrLarger } = useBreakpoints();
  const [isDarkMode] = useIsDarkMode();
  const handleClose = useCallback(() => setAnchorEl(null), []);

  return (
    <>
      <ProcessTilesControlsGlobalStyles />
      <ProcessTilesControlsStyles className="doNotPrint">
        <div className="topRow">
          {isCustomerWithExpandableTile && (
            <>
              <div className="relative">
                <BtnShowProcessTilesConfig
                  handleClick={event => setAnchorEl(event.currentTarget)}
                />
                <div id="process-tiles-config" style={{ position: "absolute" }}>
                  <ProcessTilesConfigMenu {...{ anchorEl, handleClose }} />
                </div>
              </div>
            </>
          )}
          <div className="filterControls">
            <ProcessTilesFilterControls />
          </div>
        </div>
        {!isTabletOrLarger && (
          <div className="filterControls secondRow">
            <ProcessTilesFilterControlsStyles $isDarkMode={isDarkMode}>
              <ProcessTilesSortFilterGroupByDropdowns />
            </ProcessTilesFilterControlsStyles>
          </div>
        )}
        <div className="filterIndicators bottomRow">
          <ProcessTilesFilterIndicator />
        </div>
      </ProcessTilesControlsStyles>
    </>
  );
};

export default ProcessTilesControls;

const ProcessTilesControlsStyles = styled.div`
  position: absolute;
  z-index: ${Z_INDEX.L1};
  left: 16px;
  right: 24px;
  top: 16px;
  display: flex;
  flex-direction: column;
  .btnExpand {
    margin-right: 8px;
  }
  .relative {
    position: relative;
    margin-right: 8px;
  }
  .topRow,
  .bottomRow {
    display: flex;
  }
  .bottomRow {
    padding: 16px;
  }
  .filterControls {
    margin-left: 16px;
    flex-grow: 1;
    &.secondRow {
      margin-left: 12px;
      margin-top: 8px;
      margin-bottom: -15px;
    }
  }
  .filterIndicators {
    /* add space above when sort-filter-group controls wrap around to second row */
    @media (max-width: ${BREAKPOINT_TABLET}px) {
      margin-top: 6px;
    }
  }
`;

function BtnShowProcessTilesConfig({
  handleClick,
}: {
  handleClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
}) {
  const { translate } = useFlightTranslation();
  return (
    <Tooltip title={translate("Configure_Tiles")} disableInteractive={true}>
      <IconButton size="small" onClick={handleClick}>
        <MoreVert />
      </IconButton>
    </Tooltip>
  );
}

const ProcessTilesControlsGlobalStyles = createGlobalStyle`
@media (max-width: ${BREAKPOINT_TABLET}px) {
  .tilesWrapper{
    margin-top: 8px;
  }
}
`;
